import { type RefObject, useEffect, useState } from 'react'

interface UseIsScrolledOptions {
  enabled?: boolean
}

/**
 * Tracks whether a scrollable element is scrolled past its top edge.
 * Useful for toggling a drop shadow on a fixed header when content has
 * scrolled underneath it.
 */
export function useIsScrolled(
  scrollRef: RefObject<HTMLElement | null>,
  { enabled = true }: UseIsScrolledOptions = {},
): boolean {
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    if (!enabled) {
      setScrolled(false)
      return
    }
    let animationFrame: number | null = null
    let attachedElement: HTMLElement | null = null

    function onScroll() {
      if (!attachedElement) {
        return
      }
      setScrolled(attachedElement.scrollTop > 0)
    }

    function attachWhenReady() {
      const el = scrollRef.current
      if (!el) {
        animationFrame = window.requestAnimationFrame(attachWhenReady)
        return
      }
      attachedElement = el
      setScrolled(el.scrollTop > 0)
      el.addEventListener('scroll', onScroll, { passive: true })
    }

    attachWhenReady()
    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame)
      }
      attachedElement?.removeEventListener('scroll', onScroll)
    }
  }, [enabled, scrollRef])

  return scrolled
}
